/**
 * Calendar Event Unified Editor Component
 * Single-page editor layout with inline panels for extra options
 */

import { useState, useEffect, useRef } from 'react'
import {
  Box,
  TextField,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Stack,
  Collapse,
  Badge,
  FormControlLabel,
  Switch,
  useMediaQuery,
  useTheme,
  Button,
  Alert,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Card,
  CardMedia,
  CardActions,
  LinearProgress,
  Skeleton,
  Autocomplete,
  InputAdornment,
  CircularProgress,
  Divider,
} from '@mui/material'
import {
  LocationOn,
  LocalOffer,
  Folder,
  Settings,
  ExpandMore,
  ExpandLess,
  CalendarMonth,
  DateRange,
  AccessTime,
  MyLocation,
  Clear,
  Map,
  Close,
  CloudUpload,
  Delete,
  CheckCircle,
  Image as ImageIcon,
  People,
  Public,
  Lock,
  Link as LinkIcon,
  Visibility,
  HowToReg,
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'
import { TableRow, TableCell, TableHeader } from '@tiptap/extension-table'
import { BorderlessTable } from '@/features/adventures/components/editor/extensions/BorderlessTable'
import Image from '@tiptap/extension-image'
import { useQuery } from '@tanstack/react-query'
import { format, isSameDay, setHours, setMinutes } from 'date-fns'
import {
  useCalendarEventEditorStore,
  selectFormData,
  selectValidation,
  selectSelectedTags,
} from '../../stores/calendarEventEditorStore'
import { EVENT_LIMITS } from '@api/types/calendarEvent'
import { TipTapToolbar } from '@/features/adventures/components/editor/panels/TipTapToolbar'
import '@/features/adventures/components/editor/styles/DescriptionPanel.css'
import { useCachedTags } from '@/hooks/useCachedTags'
import type { TagCategoryDto, TagDto } from '@/features/adventures/types/adventure-editor.types'
import {
  useCalendarEventFiles,
  useCalendarEventStorageQuota,
  useUploadCalendarEventFile,
  useDeleteCalendarEventFile,
  useSetCalendarEventCoverImage,
} from '../../hooks/useCalendarEventFiles'
import {
  CalendarEventMediaFileDto,
  formatFileSize,
  isAllowedEventImageType,
  EVENT_STORAGE_LIMIT_BYTES,
} from '@api/types/calendarEventFiles'
import { adventuresApi } from '@api/endpoints/adventures'
import { AdventureDto } from '@api/types/adventure'
import { useEventSubscriptionLimits } from '../../hooks/useCalendarEvents'
import EventCriteriaPanel from './panels/EventCriteriaPanel'

// TagType enum value for Adventures
const TAG_TYPE_ADVENTURES = 3

// Panel types
type ActivePanel = 'location' | 'tags' | 'files' | 'settings' | 'criteria' | null

// Query to search adventures for linking
function useSearchAdventures(search: string) {
  return useQuery({
    queryKey: ['adventures', 'search', search],
    queryFn: async () => {
      const result = await adventuresApi.searchAdventures({
        searchTerm: search,
        pageSize: 10,
        myAdventuresOnly: true,
      })
      if (result && 'items' in result) return result.items as AdventureDto[]
      if (result && 'adventures' in (result as any)) return (result as any).adventures as AdventureDto[]
      return []
    },
    enabled: search.length > 2,
    staleTime: 1000 * 30,
  })
}

interface CalendarEventUnifiedEditorProps {
  eventId?: string
}

export function CalendarEventUnifiedEditor({ eventId }: CalendarEventUnifiedEditorProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const formData = useCalendarEventEditorStore(selectFormData)
  const validation = useCalendarEventEditorStore(selectValidation)
  const selectedTags = useCalendarEventEditorStore(selectSelectedTags)
  const updateFormData = useCalendarEventEditorStore((state) => state.updateFormData)
  const toggleTag = useCalendarEventEditorStore((state) => state.toggleTag)

  // Active panel state (only one can be open at a time)
  const [activePanel, setActivePanel] = useState<ActivePanel>(null)

  // Multi-day mode
  const [isMultiDay, setIsMultiDay] = useState(() => {
    if (formData.startDateTime && formData.endDateTime) {
      return !isSameDay(formData.startDateTime, formData.endDateTime)
    }
    return false
  })

  // Location panel state
  const [isGettingLocation, setIsGettingLocation] = useState(false)
  const [locationError, setLocationError] = useState<string | null>(null)

  // Files panel state
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Settings panel state
  const [adventureSearch, setAdventureSearch] = useState('')

  // Tags data
  const { categories, isLoading: isLoadingTags, error: tagsError, getTag } = useCachedTags()
  const eventCategories = categories.filter(
    (category) => category.tagType === TAG_TYPE_ADVENTURES && category.isActive
  )

  // Files data
  const { data: filesData, isLoading: isLoadingFiles } = useCalendarEventFiles(eventId || '')
  const { data: quotaData, isLoading: isLoadingQuota } = useCalendarEventStorageQuota(eventId || '')
  const uploadMutation = useUploadCalendarEventFile()
  const deleteMutation = useDeleteCalendarEventFile()
  const setCoverMutation = useSetCalendarEventCoverImage()
  const files = (filesData as any)?.files || []
  const usedBytes = (quotaData as any)?.usedBytes || 0
  const usedPercentage = (quotaData as any)?.usedPercentage || 0

  // Settings data
  const { data: adventures, isLoading: isSearchingAdventures } = useSearchAdventures(adventureSearch)
  const { data: subscriptionLimitsData } = useEventSubscriptionLimits()
  const subscriptionLimits = subscriptionLimitsData as import('@api/types/calendarEvent').EventSubscriptionLimitsDto | undefined
  const maxEventSize = subscriptionLimits?.maxEventSize || 0

  // Update multi-day state when form data changes
  useEffect(() => {
    if (formData.startDateTime && formData.endDateTime) {
      const multiDay = !isSameDay(formData.startDateTime, formData.endDateTime)
      if (multiDay !== isMultiDay) {
        setIsMultiDay(multiDay)
      }
    }
  }, [formData.startDateTime, formData.endDateTime])

  // Track if we're updating from store to prevent loops
  const isStoreUpdateRef = useRef(false)

  // Initialize TipTap editor for full description
  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          link: false,
        }),
        Link.configure({
          openOnClick: false,
        }),
        Underline,
        TextAlign.configure({
          types: ['heading', 'paragraph'],
        }),
        BorderlessTable.configure({ resizable: true }),
        TableRow,
        TableCell,
        TableHeader,
        Image.configure({
          inline: false,
          allowBase64: true,
        }),
      ],
      content: formData.fullDescription || '',
      onUpdate: ({ editor }) => {
        if (isStoreUpdateRef.current) return
        const html = editor.getHTML()
        const isEmpty = html === '<p></p>' || html === ''
        updateFormData({ fullDescription: isEmpty ? '' : html })
      },
    },
    []
  )

  // Sync editor content when formData changes
  useEffect(() => {
    if (editor && formData.fullDescription !== editor.getHTML()) {
      const currentContent = editor.getHTML()
      const newContent = formData.fullDescription || ''
      const currentEmpty = currentContent === '<p></p>' || currentContent === ''
      const newEmpty = newContent === '<p></p>' || newContent === ''
      if (currentEmpty && newEmpty) return
      isStoreUpdateRef.current = true
      editor.commands.setContent(newContent)
      queueMicrotask(() => {
        isStoreUpdateRef.current = false
      })
    }
  }, [formData.fullDescription, editor])

  // Cleanup
  useEffect(() => {
    return () => {
      editor?.destroy()
    }
  }, [editor])

  // Toggle panel
  const handleTogglePanel = (panel: ActivePanel) => {
    setActivePanel(activePanel === panel ? null : panel)
  }

  // Handle multi-day toggle
  const handleMultiDayToggle = () => {
    if (isMultiDay) {
      if (formData.startDateTime && formData.endDateTime) {
        const startDate = formData.startDateTime
        const endTime = formData.endDateTime
        const newEndDateTime = setHours(
          setMinutes(new Date(startDate), endTime.getMinutes()),
          endTime.getHours()
        )
        updateFormData({ endDateTime: newEndDateTime })
      }
    }
    setIsMultiDay(!isMultiDay)
  }

  // Handle start date change
  const handleStartDateChange = (date: Date | null) => {
    if (!date) return
    let newStartDateTime = date
    if (formData.startDateTime) {
      newStartDateTime = setHours(
        setMinutes(date, formData.startDateTime.getMinutes()),
        formData.startDateTime.getHours()
      )
    }
    updateFormData({ startDateTime: newStartDateTime })
    if (!isMultiDay && formData.endDateTime) {
      const newEndDateTime = setHours(
        setMinutes(date, formData.endDateTime.getMinutes()),
        formData.endDateTime.getHours()
      )
      updateFormData({ endDateTime: newEndDateTime })
    }
  }

  // Handle start time change
  const handleStartTimeChange = (time: Date | null) => {
    if (!time || !formData.startDateTime) return
    const newStartDateTime = setHours(
      setMinutes(formData.startDateTime, time.getMinutes()),
      time.getHours()
    )
    updateFormData({ startDateTime: newStartDateTime })
  }

  // Handle end time change
  const handleEndTimeChange = (time: Date | null) => {
    if (!time || !formData.endDateTime) return
    const newEndDateTime = setHours(
      setMinutes(formData.endDateTime, time.getMinutes()),
      time.getHours()
    )
    updateFormData({ endDateTime: newEndDateTime })
  }

  // Handle end date change (for multi-day mode)
  const handleEndDateChange = (date: Date | null) => {
    if (!date || !formData.endDateTime) return
    const newEndDateTime = setHours(
      setMinutes(date, formData.endDateTime.getMinutes()),
      formData.endDateTime.getHours()
    )
    updateFormData({ endDateTime: newEndDateTime })
  }

  // Location handlers
  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser')
      return
    }
    setIsGettingLocation(true)
    setLocationError(null)
    navigator.geolocation.getCurrentPosition(
      (position) => {
        updateFormData({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        })
        setIsGettingLocation(false)
      },
      (error) => {
        setLocationError(
          error.code === 1 ? 'Location access was denied' : 'Failed to get your location'
        )
        setIsGettingLocation(false)
      }
    )
  }

  const handleClearCoordinates = () => {
    updateFormData({ latitude: null, longitude: null })
  }

  // Tags handlers
  const handleToggleTag = (tagId: string, selectionMode: 'single' | 'multiple', categoryId: string) => {
    if (selectionMode === 'single') {
      const category = eventCategories.find((c) => c.id === categoryId)
      if (category) {
        const categoryTagIds = category.tags.map((t) => t.id)
        const otherSelectedInCategory = selectedTags.filter(
          (id) => categoryTagIds.includes(id) && id !== tagId
        )
        otherSelectedInCategory.forEach((id) => toggleTag(id))
      }
    }
    toggleTag(tagId)
  }

  const findTagAndCategory = (tagId: string): { tag: TagDto; category: TagCategoryDto } | null => {
    for (const category of eventCategories) {
      const tag = category.tags?.find((t) => t.id === tagId)
      if (tag) return { tag, category }
    }
    return null
  }

  const getTagChipStyle = (categoryColor?: string, isSelected?: boolean) => {
    if (isSelected) {
      return {
        backgroundColor: categoryColor || '#1976d2',
        color: 'white',
        borderColor: categoryColor || '#1976d2',
      }
    }
    return {
      borderColor: categoryColor || '#ccc',
      color: categoryColor || '#666',
    }
  }

  // Files handlers
  const handleFileSelect = async (fileList: FileList | null) => {
    if (!fileList || !eventId) return
    setUploadError(null)
    const acceptedFiles = Array.from(fileList)
    for (const file of acceptedFiles) {
      if (!isAllowedEventImageType(file.type)) {
        setUploadError(`File type not allowed: ${file.type}. Only images are accepted.`)
        continue
      }
      const remainingBytes = EVENT_STORAGE_LIMIT_BYTES - usedBytes
      if (file.size > remainingBytes) {
        setUploadError(
          `Not enough storage. File size: ${formatFileSize(file.size)}, Remaining: ${formatFileSize(remainingBytes)}`
        )
        continue
      }
      try {
        await uploadMutation.mutateAsync({ eventId, file })
      } catch (error) {
        setUploadError('Failed to upload file. Please try again.')
      }
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    handleFileSelect(e.dataTransfer.files)
  }

  const handleDeleteFile = async (fileId: string) => {
    if (!eventId) return
    if (formData.coverImageId === fileId) {
      updateFormData({ coverImageId: null, coverImageUrl: null })
    }
    await deleteMutation.mutateAsync({ eventId, fileId })
  }

  const handleSetCover = async (file: CalendarEventMediaFileDto) => {
    if (!eventId) return
    try {
      await setCoverMutation.mutateAsync({ eventId, mediaFileId: file.id })
      updateFormData({ coverImageId: file.id, coverImageUrl: file.fileUrl })
    } catch (error) {
      console.error('Failed to set cover image:', error)
    }
  }

  const handleClearCover = async () => {
    if (!eventId) return
    try {
      await setCoverMutation.mutateAsync({ eventId, mediaFileId: null })
      updateFormData({ coverImageId: null, coverImageUrl: null })
    } catch (error) {
      console.error('Failed to clear cover image:', error)
    }
  }

  // Count indicators for toolbar buttons
  const hasLocation = !!formData.locationName || (formData.latitude !== null && formData.longitude !== null)
  const tagsCount = selectedTags.length
  const hasSettings = formData.maxAttendees !== null || formData.linkedAdventureId !== null
  const hasCoordinates = formData.latitude !== null && formData.longitude !== null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 1.5 : 3 }}>
      {/* Toolbar for Location, Tags, Files, Settings - At the top */}
      <Paper sx={{ p: isMobile ? 1 : 2 }}>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ gap: isMobile ? 0.5 : 1 }}>
          {/* Location Button */}
          <Tooltip title="Edit Location">
            <IconButton
              onClick={() => handleTogglePanel('location')}
              color={activePanel === 'location' ? 'primary' : hasLocation ? 'primary' : 'default'}
              sx={{
                border: 1,
                borderColor: activePanel === 'location' ? 'primary.main' : hasLocation ? 'primary.main' : 'divider',
                borderRadius: 1,
                px: isMobile ? 1 : 2,
                gap: 0.5,
                backgroundColor: activePanel === 'location' ? 'action.selected' : 'transparent',
              }}
            >
              <LocationOn fontSize="small" />
              <Typography variant="body2" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                Location
              </Typography>
              {activePanel === 'location' ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
          </Tooltip>

          {/* Tags Button */}
          <Tooltip title="Edit Tags">
            <IconButton
              onClick={() => handleTogglePanel('tags')}
              color={activePanel === 'tags' ? 'primary' : tagsCount > 0 ? 'primary' : 'default'}
              sx={{
                border: 1,
                borderColor: activePanel === 'tags' ? 'primary.main' : tagsCount > 0 ? 'primary.main' : 'divider',
                borderRadius: 1,
                px: isMobile ? 1 : 2,
                gap: 0.5,
                backgroundColor: activePanel === 'tags' ? 'action.selected' : 'transparent',
              }}
            >
              <Badge badgeContent={tagsCount} color="primary">
                <LocalOffer fontSize="small" />
              </Badge>
              <Typography variant="body2" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>Tags</Typography>
              {activePanel === 'tags' ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
          </Tooltip>

          {/* Files Button */}
          <Tooltip title={eventId ? 'Manage Files' : 'Save event first to upload files'}>
            <span>
              <IconButton
                onClick={() => handleTogglePanel('files')}
                disabled={!eventId}
                color={activePanel === 'files' ? 'primary' : 'default'}
                sx={{
                  border: 1,
                  borderColor: activePanel === 'files' ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  px: isMobile ? 1 : 2,
                  gap: 0.5,
                  backgroundColor: activePanel === 'files' ? 'action.selected' : 'transparent',
                }}
              >
                <Folder fontSize="small" />
                <Typography variant="body2" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>Files</Typography>
                {activePanel === 'files' ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>

          {/* Settings Button */}
          <Tooltip title="Event Settings">
            <IconButton
              onClick={() => handleTogglePanel('settings')}
              color={activePanel === 'settings' ? 'primary' : hasSettings ? 'primary' : 'default'}
              sx={{
                border: 1,
                borderColor: activePanel === 'settings' ? 'primary.main' : hasSettings ? 'primary.main' : 'divider',
                borderRadius: 1,
                px: isMobile ? 1 : 2,
                gap: 0.5,
                backgroundColor: activePanel === 'settings' ? 'action.selected' : 'transparent',
              }}
            >
              <Settings fontSize="small" />
              <Typography variant="body2" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>Settings</Typography>
              {activePanel === 'settings' ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
          </Tooltip>

          {/* Criteria Button - Only show after event is saved */}
          {eventId && (
            <Tooltip title="Display & Join Criteria">
              <IconButton
                onClick={() => handleTogglePanel('criteria')}
                color={activePanel === 'criteria' ? 'primary' : 'default'}
                sx={{
                  border: 1,
                  borderColor: activePanel === 'criteria' ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  px: isMobile ? 1 : 2,
                  gap: 0.5,
                  backgroundColor: activePanel === 'criteria' ? 'action.selected' : 'transparent',
                }}
              >
                <Visibility fontSize="small" />
                <Typography variant="body2" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>Criteria</Typography>
                {activePanel === 'criteria' ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}
        </Stack>

        {/* Collapsible Panel Content */}
        <Collapse in={activePanel !== null}>
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            {/* Location Panel */}
            {activePanel === 'location' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Location Name"
                  value={formData.locationName}
                  onChange={(e) => updateFormData({ locationName: e.target.value })}
                  helperText={`${formData.locationName.length}/${EVENT_LIMITS.LOCATION_NAME_MAX_LENGTH} characters`}
                  inputProps={{ maxLength: EVENT_LIMITS.LOCATION_NAME_MAX_LENGTH }}
                  fullWidth
                  size="small"
                  placeholder="e.g., Central Park, Main Conference Room, Online via Zoom"
                />

                <Box>
                  <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    Map Location (Optional)
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap" useFlexGap>
                    <TextField
                      label="Latitude"
                      type="number"
                      value={formData.latitude ?? ''}
                      onChange={(e) =>
                        updateFormData({ latitude: e.target.value ? parseFloat(e.target.value) : null })
                      }
                      size="small"
                      inputProps={{ step: 'any', min: -90, max: 90 }}
                      sx={{ width: 120 }}
                    />
                    <TextField
                      label="Longitude"
                      type="number"
                      value={formData.longitude ?? ''}
                      onChange={(e) =>
                        updateFormData({ longitude: e.target.value ? parseFloat(e.target.value) : null })
                      }
                      size="small"
                      inputProps={{ step: 'any', min: -180, max: 180 }}
                      sx={{ width: 120 }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<MyLocation />}
                      onClick={handleGetCurrentLocation}
                      disabled={isGettingLocation}
                    >
                      {isGettingLocation ? 'Getting...' : 'Current'}
                    </Button>
                    {hasCoordinates && (
                      <IconButton size="small" onClick={handleClearCoordinates} title="Clear coordinates">
                        <Clear />
                      </IconButton>
                    )}
                  </Stack>
                  {locationError && (
                    <Alert severity="error" sx={{ mt: 1 }}>{locationError}</Alert>
                  )}
                  {hasCoordinates && (
                    <Button
                      component="a"
                      href={`https://www.google.com/maps?q=${formData.latitude},${formData.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="text"
                      size="small"
                      startIcon={<Map />}
                      sx={{ mt: 1 }}
                    >
                      View on Google Maps
                    </Button>
                  )}
                </Box>
              </Box>
            )}

            {/* Tags Panel */}
            {activePanel === 'tags' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {isLoadingTags ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Loading tags...</Typography>
                  </Box>
                ) : tagsError ? (
                  <Alert severity="error">Failed to load tags.</Alert>
                ) : (
                  <>
                    {/* Selected Tags */}
                    {selectedTags.length > 0 && (
                      <Box sx={{ p: 1.5, backgroundColor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                          Selected ({selectedTags.length})
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selectedTags.map((tagId) => {
                            const result = findTagAndCategory(tagId)
                            const cachedTag = getTag(tagId)
                            return (
                              <Chip
                                key={tagId}
                                label={result?.tag.displayName || cachedTag?.displayName || tagId}
                                onDelete={() => toggleTag(tagId)}
                                size="small"
                                sx={{
                                  backgroundColor: result?.category.color || 'primary.main',
                                  color: 'white',
                                  '& .MuiChip-deleteIcon': { color: 'white' },
                                }}
                              />
                            )
                          })}
                        </Box>
                      </Box>
                    )}

                    {/* Tag Categories */}
                    {eventCategories
                      .filter((c) => c.tags?.some((t) => t.isActive))
                      .map((category) => (
                        <Accordion key={category.id} defaultExpanded={false} disableGutters sx={{ '&:before': { display: 'none' } }}>
                          <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {category.icon && <span>{category.icon}</span>}
                              <Typography variant="body2" sx={{ color: category.color, fontWeight: 500 }}>
                                {category.displayName}
                              </Typography>
                              {category.selectionMode === 'single' && (
                                <Chip label="Single" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                              )}
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 0 }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {category.tags
                                ?.filter((t) => t.isActive)
                                .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || a.displayName.localeCompare(b.displayName))
                                .map((tag) => {
                                  const isSelected = selectedTags.includes(tag.id)
                                  return (
                                    <Chip
                                      key={tag.id}
                                      label={tag.displayName}
                                      onClick={() => handleToggleTag(tag.id, category.selectionMode, category.id)}
                                      variant={isSelected ? 'filled' : 'outlined'}
                                      size="small"
                                      sx={getTagChipStyle(category.color, isSelected)}
                                    />
                                  )
                                })}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      ))}
                  </>
                )}
              </Box>
            )}

            {/* Files Panel */}
            {activePanel === 'files' && eventId && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Storage Quota */}
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">Storage</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {isLoadingQuota ? '...' : `${formatFileSize(usedBytes)} / ${formatFileSize(EVENT_STORAGE_LIMIT_BYTES)}`}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={usedPercentage}
                    color={usedPercentage > 90 ? 'error' : usedPercentage > 75 ? 'warning' : 'primary'}
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>

                {uploadError && (
                  <Alert severity="error" onClose={() => setUploadError(null)}>{uploadError}</Alert>
                )}

                {/* Upload Zone */}
                <Box
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    border: '2px dashed',
                    borderColor: isDragOver ? 'primary.main' : 'grey.300',
                    borderRadius: 1,
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: isDragOver ? 'action.hover' : 'transparent',
                    '&:hover': { borderColor: 'primary.main', backgroundColor: 'action.hover' },
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    multiple
                    onChange={(e) => handleFileSelect(e.target.files)}
                    style={{ display: 'none' }}
                  />
                  <CloudUpload sx={{ fontSize: 32, color: 'action.active', mb: 0.5 }} />
                  <Typography variant="body2">
                    {isDragOver ? 'Drop files here' : 'Drop images or click to upload'}
                  </Typography>
                </Box>

                {uploadMutation.isPending && <LinearProgress />}

                {/* Current Cover */}
                {formData.coverImageUrl && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" gutterBottom display="block">Cover Image</Typography>
                    <Card sx={{ maxWidth: 200 }}>
                      <CardMedia component="img" height="100" image={formData.coverImageUrl} alt="Cover" />
                      <CardActions sx={{ p: 0.5 }}>
                        <Button size="small" color="error" onClick={handleClearCover} disabled={setCoverMutation.isPending}>
                          Remove
                        </Button>
                      </CardActions>
                    </Card>
                  </Box>
                )}

                {/* File Grid */}
                {isLoadingFiles ? (
                  <Grid container spacing={1}>
                    {[1, 2, 3].map((i) => (
                      <Grid item xs={4} sm={3} key={i}>
                        <Skeleton variant="rectangular" height={80} />
                      </Grid>
                    ))}
                  </Grid>
                ) : files.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" textAlign="center">No files uploaded</Typography>
                ) : (
                  <Grid container spacing={1}>
                    {files.map((file: CalendarEventMediaFileDto) => (
                      <Grid item xs={4} sm={3} key={file.id}>
                        <Card
                          sx={{
                            position: 'relative',
                            outline: formData.coverImageId === file.id ? '2px solid' : 'none',
                            outlineColor: 'primary.main',
                          }}
                        >
                          {formData.coverImageId === file.id && (
                            <CheckCircle
                              sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                fontSize: 16,
                                color: 'primary.main',
                                backgroundColor: 'white',
                                borderRadius: '50%',
                              }}
                            />
                          )}
                          <CardMedia
                            component="img"
                            height={80}
                            image={file.thumbnailUrl || file.fileUrl}
                            alt={file.displayName || file.fileName}
                            sx={{ objectFit: 'cover' }}
                          />
                          <CardActions sx={{ justifyContent: 'space-between', p: 0.5 }}>
                            {formData.coverImageId !== file.id && (
                              <Button size="small" onClick={() => handleSetCover(file)} disabled={setCoverMutation.isPending} sx={{ fontSize: '0.65rem', minWidth: 0, px: 0.5 }}>
                                Cover
                              </Button>
                            )}
                            <IconButton size="small" color="error" onClick={() => handleDeleteFile(file.id)} disabled={deleteMutation.isPending}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            )}

            {/* Settings Panel */}
            {activePanel === 'settings' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Privacy */}
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isPublic}
                      onChange={(e) => updateFormData({ isPublic: e.target.checked })}
                      size="small"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {formData.isPublic ? <Public fontSize="small" /> : <Lock fontSize="small" />}
                      <Typography variant="body2">{formData.isPublic ? 'Public event' : 'Private event'}</Typography>
                    </Box>
                  }
                />

                {/* Max Attendees */}
                <TextField
                  label="Max Attendees"
                  type="number"
                  value={formData.maxAttendees ?? ''}
                  onChange={(e) =>
                    updateFormData({ maxAttendees: e.target.value ? parseInt(e.target.value, 10) : null })
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <People fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{ min: 1, max: maxEventSize > 0 ? maxEventSize : undefined }}
                  helperText={maxEventSize > 0 ? `Max ${maxEventSize} per subscription` : 'Leave empty for unlimited'}
                  size="small"
                  sx={{ width: 200 }}
                />

                {/* Linked Adventure */}
                <Box>
                  <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    Linked Adventure (Optional)
                  </Typography>
                  <Autocomplete
                    options={adventures || []}
                    getOptionLabel={(option: AdventureDto) => option.title}
                    value={
                      formData.linkedAdventureId
                        ? adventures?.find((a) => a.id === formData.linkedAdventureId) || null
                        : null
                    }
                    onChange={(_, value) => updateFormData({ linkedAdventureId: value?.id || null })}
                    onInputChange={(_, value) => setAdventureSearch(value)}
                    loading={isSearchingAdventures}
                    size="small"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search your adventures..."
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <LinkIcon fontSize="small" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    noOptionsText={adventureSearch.length < 3 ? 'Type 3+ characters' : 'No adventures found'}
                    sx={{ maxWidth: 350 }}
                  />
                </Box>
              </Box>
            )}

            {/* Criteria Panel */}
            {activePanel === 'criteria' && eventId && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Display Criteria */}
                <EventCriteriaPanel
                  type="display"
                  eventId={eventId}
                  title="Display Criteria"
                  description="Who can see this event in search results"
                  icon={<Visibility color="primary" />}
                />

                <Divider />

                {/* Join Criteria */}
                <EventCriteriaPanel
                  type="join"
                  eventId={eventId}
                  title="Join Criteria"
                  description="Who can RSVP to this event"
                  icon={<HowToReg color="primary" />}
                />
              </Box>
            )}
          </Box>
        </Collapse>
      </Paper>

      {/* Title & Quick Description */}
      <Paper sx={{ p: isMobile ? 1.5 : 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Event Title"
            value={formData.title}
            onChange={(e) => updateFormData({ title: e.target.value })}
            error={!!validation.errors.title}
            helperText={
              validation.errors.title ||
              `${formData.title.length}/${EVENT_LIMITS.TITLE_MAX_LENGTH} characters`
            }
            inputProps={{ maxLength: EVENT_LIMITS.TITLE_MAX_LENGTH }}
            fullWidth
            required
            variant="outlined"
            size={isMobile ? 'small' : 'medium'}
            sx={{ '& .MuiInputBase-input': { fontSize: isMobile ? '1rem' : '1.25rem', fontWeight: 500 } }}
          />

          <TextField
            label="Quick Description"
            value={formData.quickDescription}
            onChange={(e) => updateFormData({ quickDescription: e.target.value })}
            error={!!validation.errors.quickDescription}
            helperText={
              validation.errors.quickDescription ||
              `${formData.quickDescription.length}/${EVENT_LIMITS.QUICK_DESCRIPTION_MAX_LENGTH} characters`
            }
            inputProps={{ maxLength: EVENT_LIMITS.QUICK_DESCRIPTION_MAX_LENGTH }}
            fullWidth
            required
            multiline
            rows={2}
            size={isMobile ? 'small' : 'medium'}
            placeholder="Brief summary shown in event listings"
          />
        </Box>
      </Paper>

      {/* Date & Time Section */}
      <Paper sx={{ p: isMobile ? 1.5 : 3 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 1.5 : 2 }}>
            {/* Header with multi-day toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarMonth color="action" fontSize={isMobile ? 'small' : 'medium'} />
                <Typography variant={isMobile ? 'body1' : 'subtitle1'} fontWeight={500}>
                  Date & Time
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isAllDay}
                      onChange={(e) => updateFormData({ isAllDay: e.target.checked })}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">All day</Typography>}
                  sx={{ mr: 0 }}
                />
                <Tooltip title={isMultiDay ? 'Switch to single day' : 'Switch to multiple days'}>
                  <IconButton onClick={handleMultiDayToggle} size="small">
                    {isMultiDay ? <CalendarMonth fontSize="small" /> : <DateRange fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Single Day Mode */}
            {!isMultiDay && (
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <DatePicker
                  label="Date"
                  value={formData.startDateTime}
                  onChange={handleStartDateChange}
                  slotProps={{
                    textField: {
                      size: 'small',
                      error: !!validation.errors.startDateTime,
                      sx: { width: isMobile ? 150 : 150 },
                    },
                  }}
                />
                {!formData.isAllDay && (
                  <>
                    <TimePicker
                      label="Start"
                      value={formData.startDateTime}
                      onChange={handleStartTimeChange}
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: { width: isMobile ? 135 : 120 },
                        },
                      }}
                    />
                    <Typography variant="body2" sx={{ color: 'text.secondary', px: 0.5 }}>-</Typography>
                    <TimePicker
                      label="End"
                      value={formData.endDateTime}
                      onChange={handleEndTimeChange}
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: { width: isMobile ? 135 : 120 },
                        },
                      }}
                    />
                  </>
                )}
              </Stack>
            )}

            {/* Multi-Day Mode */}
            {isMultiDay && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <DatePicker
                    label="Start"
                    value={formData.startDateTime}
                    onChange={handleStartDateChange}
                    slotProps={{
                      textField: {
                        size: 'small',
                        error: !!validation.errors.startDateTime,
                        sx: { width: isMobile ? 150 : 150 },
                      },
                    }}
                  />
                  {!formData.isAllDay && (
                    <TimePicker
                      label="Time"
                      value={formData.startDateTime}
                      onChange={handleStartTimeChange}
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: { width: isMobile ? 135 : 120 },
                        },
                      }}
                    />
                  )}
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <DatePicker
                    label="End"
                    value={formData.endDateTime}
                    onChange={handleEndDateChange}
                    minDate={formData.startDateTime || undefined}
                    slotProps={{
                      textField: {
                        size: 'small',
                        error: !!validation.errors.endDateTime,
                        sx: { width: isMobile ? 150 : 150 },
                      },
                    }}
                  />
                  {!formData.isAllDay && (
                    <TimePicker
                      label="Time"
                      value={formData.endDateTime}
                      onChange={handleEndTimeChange}
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: { width: isMobile ? 135 : 120 },
                        },
                      }}
                    />
                  )}
                </Stack>
              </Box>
            )}

          </Box>
        </LocalizationProvider>
      </Paper>

      {/* Full Description */}
      <Paper sx={{ p: isMobile ? 1.5 : 3 }}>
        <Typography variant={isMobile ? 'body1' : 'subtitle1'} fontWeight={500} gutterBottom>
          Full Description
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: isMobile ? 1 : 2 }}>
          Detailed information shown on the event detail page
        </Typography>
        <Box className="rich-editor-container" sx={{ overflow: 'visible' }}>
          {editor && <TipTapToolbar editor={editor} />}
          <EditorContent editor={editor} className="tiptap-editor" />
        </Box>
      </Paper>
    </Box>
  )
}

export default CalendarEventUnifiedEditor
